/**
 * Account Data Service
 *
 * アカウント別データ管理サービス
 * LocalStorageをアカウントIDごとに分離して管理
 *
 * @description
 * - test0513@salonhub.co.jp: デモデータ付き
 * - その他のアカウント: クリーンな環境
 */

import logger from '../utils/logger';
import { ExtendedCustomer } from '../types/salonboard';

export class AccountDataService {
  /**
   * テストアカウント定義
   */
  private static readonly TEST_ACCOUNT_ID = 'test-account-0513';

  /**
   * 現在ログイン中のアカウントID
   */
  private static currentAccountId: string | null = null;

  /**
   * アカウントIDを設定
   */
  static setCurrentAccount(accountId: string, email: string): void {
    this.currentAccountId = accountId;
    localStorage.setItem('salonhub_current_account_id', accountId);
    localStorage.setItem('salonhub_current_account_email', email);
    logger.log(`📋 [AccountDataService] アカウント設定: ${email} (ID: ${accountId})`);
  }

  /**
   * 現在のアカウントIDを取得
   */
  static getCurrentAccountId(): string | null {
    if (!this.currentAccountId) {
      this.currentAccountId = localStorage.getItem('salonhub_current_account_id');
    }
    return this.currentAccountId;
  }

  /**
   * 現在のアカウントメールアドレスを取得
   */
  static getCurrentAccountEmail(): string | null {
    return localStorage.getItem('salonhub_current_account_email');
  }

  /**
   * テストアカウントかどうか判定
   */
  /**
   * テストアカウントかどうか判定
   * test0513@salonhub.co.jpのみをテストアカウント（データあり）として判定
   * test0701@salonhub.co.jpは明示的に空データアカウントとして扱う
   */
  static isTestAccount(accountId?: string): boolean {
    const email = this.getCurrentAccountEmail();
    
    // emailベースで判定：test0513のみtrue
    if (email === 'test0513@salonhub.co.jp') {
      return true;
    }
    
    // test0701は明示的にfalse（空データ）
    if (email === 'test0701@salonhub.co.jp') {
      return false;
    }
    
    // 後方互換性のため、従来のIDベースチェックも残す
    const id = accountId || this.getCurrentAccountId();
    return id === this.TEST_ACCOUNT_ID;
  }

  /**
   * アカウント別LocalStorageキー生成
   */
  private static getAccountKey(baseKey: string, accountId?: string): string {
    const id = accountId || this.getCurrentAccountId();
    if (!id) {
      logger.warn(`⚠️ [AccountDataService] アカウントIDが設定されていません。デフォルトキーを使用: ${baseKey}`);
      return baseKey;
    }
    return `${baseKey}_${id}`;
  }

  /**
   * アカウント別データ保存
   */
  static setAccountData<T>(baseKey: string, data: T, accountId?: string): void {
    try {
      const key = this.getAccountKey(baseKey, accountId);
      const jsonData = JSON.stringify(data);
      localStorage.setItem(key, jsonData);
      logger.log(`💾 [AccountDataService] データ保存: ${key} (${jsonData.length} bytes)`);
    } catch (error) {
      logger.error(`❌ [AccountDataService] データ保存エラー: ${baseKey}`, error);
    }
  }

  /**
   * アカウント別データ取得
   */
  static getAccountData<T>(baseKey: string, defaultValue: T, accountId?: string): T {
    try {
      const key = this.getAccountKey(baseKey, accountId);
      const jsonData = localStorage.getItem(key);

      if (!jsonData) {
        logger.log(`📂 [AccountDataService] データなし: ${key} → デフォルト値使用`);
        return defaultValue;
      }

      const data = JSON.parse(jsonData) as T;
      logger.log(`📥 [AccountDataService] データ取得: ${key}`);
      return data;
    } catch (error) {
      logger.error(`❌ [AccountDataService] データ取得エラー: ${baseKey}`, error);
      return defaultValue;
    }
  }

  /**
   * アカウント別データ削除
   */
  static removeAccountData(baseKey: string, accountId?: string): void {
    try {
      const key = this.getAccountKey(baseKey, accountId);
      localStorage.removeItem(key);
      logger.log(`🗑️ [AccountDataService] データ削除: ${key}`);
    } catch (error) {
      logger.error(`❌ [AccountDataService] データ削除エラー: ${baseKey}`, error);
    }
  }

  /**
   * アカウント別データ存在確認
   */
  static hasAccountData(baseKey: string, accountId?: string): boolean {
    const key = this.getAccountKey(baseKey, accountId);
    return localStorage.getItem(key) !== null;
  }

  /**
   * 全アカウントデータをクリア
   */
  static clearAllAccountData(accountId?: string): void {
    const id = accountId || this.getCurrentAccountId();
    if (!id) {
      logger.warn('⚠️ [AccountDataService] アカウントIDが設定されていません');
      return;
    }

    const keysToRemove: string[] = [];

    // LocalStorageから該当アカウントのキーを検索
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.endsWith(`_${id}`)) {
        keysToRemove.push(key);
      }
    }

    // 一括削除
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    logger.log(`🗑️ [AccountDataService] 全データクリア: ${id} (${keysToRemove.length}件)`);
  }

  /**
   * アカウントログアウト
   */
  static logout(): void {
    this.currentAccountId = null;
    localStorage.removeItem('salonhub_current_account_id');
    localStorage.removeItem('salonhub_current_account_email');
    logger.log('👋 [AccountDataService] ログアウト完了');
  }

  /**
   * デバッグ情報出力
   */
  static debugInfo(): void {
    const accountId = this.getCurrentAccountId();
    const email = this.getCurrentAccountEmail();
    const isTest = this.isTestAccount();

    console.log('=== AccountDataService Debug Info ===');
    console.log('Current Account ID:', accountId);
    console.log('Current Account Email:', email);
    console.log('Is Test Account:', isTest);
    console.log('LocalStorage Keys:');

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('customers_') || key.startsWith('treatments_') || key.startsWith('salonhub_'))) {
        const value = localStorage.getItem(key);
        console.log(`  ${key}: ${value?.substring(0, 100)}...`);
      }
    }
    console.log('=====================================');
  }

  /**
   * 顧客データの取得（アカウント別）
   */
  static getCustomers(accountId?: string): ExtendedCustomer[] {
    return this.getAccountData<ExtendedCustomer[]>('customers', [], accountId);
  }

  /**
   * 顧客データの保存（アカウント別）
   */
  static setCustomers(customers: ExtendedCustomer[], accountId?: string): void {
    this.setAccountData('customers', customers, accountId);
  }

  /**
   * 施術データの取得（アカウント別）
   */
  static getTreatments(customerId: number, accountId?: string): unknown[] {
    const key = `treatments_${customerId}`;
    return this.getAccountData<unknown[]>(key, [], accountId);
  }

  /**
   * 施術データの保存（アカウント別）
   */
  static setTreatments(customerId: number, treatments: unknown[], accountId?: string): void {
    const key = `treatments_${customerId}`;
    this.setAccountData(key, treatments, accountId);
  }

  /**
   * 初期データセットアップフラグ取得
   */
  static hasInitialData(accountId?: string): boolean {
    return this.hasAccountData('hasInitialData', accountId);
  }

  /**
   * 初期データセットアップフラグ設定
   */
  static setInitialDataFlag(value: boolean, accountId?: string): void {
    this.setAccountData('hasInitialData', value, accountId);
  }
}
